import {
  Bell,
  Bot,
  CircleUserRound,
  CreditCard,
  DatabaseZap,
  FolderGit2,
  KeyRound,
  Lock,
  Package,
  Palette,
  ScrollText,
  ShieldCheck,
  Sliders,
  TriangleAlert,
  Users,
  Wallet,
  Cpu,
} from "lucide-react";
import type { MembershipProject } from "@zworks/console-contracts";
import type { ComponentType } from "react";

/** One settings section: its URL slug, label, icon, sidebar group, and visibility. */
export interface SettingSection {
  slug: string;
  label: string;
  icon: ComponentType<{ className?: string }>;
  group: "Project" | "Policies" | "Access" | "Plan" | "Manage" | "Account";
  /** Project pages show project sections; the profile menu dialog also shows account sections. */
  scope: "project" | "account";
  ownerOnly?: boolean;
}

export const settingSections: SettingSection[] = [
  { slug: "general", label: "General", icon: Sliders, group: "Project", scope: "project" },
  {
    slug: "members",
    label: "Members and roles",
    icon: Users,
    group: "Project",
    scope: "project",
    ownerOnly: true,
  },
  {
    slug: "repositories",
    label: "Repositories",
    icon: FolderGit2,
    group: "Project",
    scope: "project",
  },
  { slug: "artifacts", label: "Artifacts", icon: Package, group: "Project", scope: "project" },
  { slug: "approvals", label: "Approvals", icon: ShieldCheck, group: "Policies", scope: "project" },
  {
    slug: "spend",
    label: "Spend and budgets",
    icon: Wallet,
    group: "Policies",
    scope: "project",
    ownerOnly: true,
  },
  { slug: "agents", label: "Agents and models", icon: Bot, group: "Policies", scope: "project" },
  {
    slug: "model-routes",
    label: "Model routes",
    icon: Cpu,
    group: "Policies",
    scope: "project",
    ownerOnly: true,
  },
  { slug: "security", label: "Security", icon: Lock, group: "Policies", scope: "project" },
  {
    slug: "data-retention",
    label: "Data and retention",
    icon: DatabaseZap,
    group: "Policies",
    scope: "project",
  },
  {
    slug: "tokens",
    label: "Tokens and webhooks",
    icon: KeyRound,
    group: "Access",
    scope: "project",
    ownerOnly: true,
  },
  { slug: "notifications", label: "Notifications", icon: Bell, group: "Access", scope: "project" },
  {
    slug: "plan-billing",
    label: "Plan and billing",
    icon: CreditCard,
    group: "Plan",
    scope: "project",
    ownerOnly: true,
  },
  {
    slug: "audit-export",
    label: "Audit and export",
    icon: ScrollText,
    group: "Plan",
    scope: "project",
    ownerOnly: true,
  },
  {
    slug: "danger-zone",
    label: "Danger zone",
    icon: TriangleAlert,
    group: "Manage",
    scope: "project",
    ownerOnly: true,
  },
  { slug: "profile", label: "Profile", icon: CircleUserRound, group: "Account", scope: "account" },
  { slug: "appearance", label: "Appearance", icon: Palette, group: "Account", scope: "account" },
];

/** Renamed slugs keep resolving so older deep links land on the new section. */
const legacyAliases: Record<string, string> = {
  "plan-and-billing": "plan-billing",
  credentials: "tokens",
  "data-controls": "data-retention",
};

export const DEFAULT_SETTING_SLUG = "general";

/**
 * Whether the role may open the section on project surfaces. Spend and budgets is read-only
 * for owners and developers ("View spend" in the mockup matrix); every other owner-only
 * section stays owner-only. Viewer and account-surface rules live in visibleSettingSections
 * and the route guard.
 */
export function canViewSettingSection(
  role: MembershipProject["role"],
  entry: SettingSection,
): boolean {
  if (entry.slug === "spend") return role === "owner" || role === "developer";
  if (entry.ownerOnly) return role === "owner";
  return true;
}

/** Finds the section for a URL slug, or null when the slug names no section. */
export function settingSectionForSlug(slug: string | undefined): SettingSection | null {
  if (!slug) return null;
  const canonical = legacyAliases[slug.toLowerCase()] ?? slug.toLowerCase();
  return settingSections.find((entry) => entry.slug === canonical) ?? null;
}

export const settingGroupOrder = [
  "Project",
  "Policies",
  "Access",
  "Plan",
  "Manage",
  "Account",
] as const;

/**
 * The sections the caller's role may see on the given surface. Owner-only sections (members,
 * tokens, plan and billing, audit exports, and the danger zone) stay owner-only; Spend and
 * budgets is read-only for developers too, matching the "View spend" row in the design mockup.
 * Viewers see only General and Artifacts. Account sections only appear in the profile menu
 * dialog, never on the project settings page.
 */
export function visibleSettingSections(
  role: MembershipProject["role"],
  surface: "page" | "dialog" = "page",
): SettingSection[] {
  return settingSections.filter((entry) => {
    if (entry.scope === "account" && surface === "page") return false;
    if (entry.scope === "account") return true;
    if (role === "viewer" && entry.slug !== "general" && entry.slug !== "artifacts") return false;
    if (!canViewSettingSection(role, entry)) return false;
    return true;
  });
}
